import PropTypes from 'prop-types';
import { Button, Icon, ListboxMultiselect } from '@evertrue/et-components';
import SuccessMetricsSection from '../create-cadence/success-metrics/success-metrics-section';
import AudienceSection from '../create-cadence/audience-section';
import UserSelector from 'apps/users/components/user-selector';

/**
 * Step 2 of the bulk message creation wizard: Audience & Metrics.
 * Renders recipient selection, success metrics, and advanced settings
 * (sharing, proposals, blueprint, tags, blackout dates).
 */
const BulkMessageAudienceStep = ({
  canDelete,
  existingAudiences,
  audiencesLoaded,
  onDisconnect,
  onDisconnectAllManual,
  contactLists,
  savedSearches,
  selectionMethod,
  setSelectionMethod,
  selectedList,
  setSelectedList,
  selectedSavedSearch,
  setSelectedSavedSearch,
  selectedContacts,
  setSelectedContacts,
  successMetric,
  setSuccessMetric,
  interactionTypes,
  portfolioTeams,
  isAdvancedOpen,
  setIsAdvancedOpen,
  isSharingOpen,
  setIsSharingOpen,
  pendingShareUsers,
  setPendingShareUsers,
  pendingSharePermission,
  setPendingSharePermission,
  sharedUsers,
  setSharedUsers,
  isProposalsOpen,
  setIsProposalsOpen,
  proposals,
  linkedProposals,
  setLinkedProposals,
  onProposalSearch,
  isBlueprintOpen,
  setIsBlueprintOpen,
  cadenceShells,
  cadenceTemplate,
  setCadenceTemplate,
  templateSearch,
  setTemplateSearch,
  templateDropdownOpen,
  setTemplateDropdownOpen,
  isTagsOpen,
  setIsTagsOpen,
  tagInput,
  setTagInput,
  tagSuggestions,
  handleAddTag,
  handleSelectTagSuggestion,
  tags,
  setTags,
  isBlackoutOpen,
  setIsBlackoutOpen,
  respectBlackoutDates,
  setRespectBlackoutDates,
}) => (
  <div className="bulk-message-audience-step">
    <div className="bulk-message-audience-step--intro">
      <h2 className="bulk-message-audience-step--title">Review Send List, Success Metric, and Settings</h2>
      <p className="bulk-message-audience-step--subtitle">
        Define who you'll send to and how you'll measure success for this outreach.
      </p>
    </div>

    {/* Select Recipients Card */}
    <div className="bulk-message-audience-step--card">
      <div className="bulk-message-audience-step--card-header">
        <Icon icon="group" className="bulk-message-audience-step--card-icon" />
        <div>
          <span>Select Recipients</span>
          <p className="create-cadence-configure--card-subtitle">
            Choose who will receive this bulk message. New recipients will be enrolled when you save.
          </p>
        </div>
      </div>
      <div className="bulk-message-audience-step--card-body">
        <AudienceSection
          canDelete={canDelete}
          existingAudiences={existingAudiences}
          audiencesLoaded={audiencesLoaded}
          contactLists={contactLists}
          savedSearches={savedSearches}
          onDisconnect={onDisconnect}
          onDisconnectAllManual={onDisconnectAllManual}
          selectionMethod={selectionMethod}
          setSelectionMethod={setSelectionMethod}
          selectedList={selectedList}
          setSelectedList={setSelectedList}
          selectedSavedSearch={selectedSavedSearch}
          setSelectedSavedSearch={setSelectedSavedSearch}
          selectedContacts={selectedContacts}
          setSelectedContacts={setSelectedContacts}
        />
      </div>
    </div>

    {/* Success Metrics - REQUIRED */}
    <div className="create-cadence-configure--card">
      <div className="create-cadence-configure--card-header">
        <div className="create-cadence-configure--card-header-left">
          <Icon icon="adjust" className="create-cadence-configure--card-icon" />
          <div>
            <h3 className="create-cadence-configure--card-title">Success Metrics</h3>
            <p className="create-cadence-configure--card-subtitle">Define how you&apos;ll measure success</p>
          </div>
        </div>
        <span className="create-cadence-configure--badge create-cadence-configure--badge-required">REQUIRED</span>
      </div>
      <div className="create-cadence-configure--card-body">
        <SuccessMetricsSection
          metric={successMetric}
          onChange={setSuccessMetric}
          interactionTypes={interactionTypes}
          savedSearches={savedSearches}
          portfolioTeams={portfolioTeams}
        />
      </div>
    </div>

    {/* Advanced Settings - OPTIONAL, collapsible (matches cadence creation pattern) */}
    <div className="create-cadence-configure--card">
      <button
        type="button"
        className="create-cadence-configure--card-header create-cadence-configure--card-header-clickable"
        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
      >
        <div className="create-cadence-configure--card-header-left">
          <Icon icon="settings" className="create-cadence-configure--card-icon" />
          <div>
            <h3 className="create-cadence-configure--card-title">Advanced Settings</h3>
            <p className="create-cadence-configure--card-subtitle">
              Sharing, proposals, blueprints, tags, and blackout dates
            </p>
          </div>
        </div>
        <div className="create-cadence-configure--advanced-right">
          <span className="create-cadence-configure--badge create-cadence-configure--badge-optional">OPTIONAL</span>
          <Icon
            icon={isAdvancedOpen ? 'chevron-up' : 'chevron-down'}
            className="create-cadence-configure--card-toggle-icon"
          />
        </div>
      </button>

      {isAdvancedOpen && (
        <div className="create-cadence-configure--card-body create-cadence-configure--advanced-body">
          {/* Sharing — collapsible (matches cadence creation) */}
          <div className="create-cadence-configure--subsection">
            <button
              type="button"
              className="create-cadence-configure--subsection-header"
              onClick={() => setIsSharingOpen(!isSharingOpen)}
            >
              <span className="create-cadence-configure--subsection-title">Sharing</span>
              <Icon
                icon={isSharingOpen ? 'chevron-up' : 'chevron-down'}
                className="create-cadence-configure--card-toggle-icon"
              />
            </button>
            {isSharingOpen && (
              <div className="create-cadence-configure--subsection-body">
                <p className="create-cadence-configure--section-desc">Control who can access this bulk message</p>

                {/* Add people */}
                <div className="create-cadence-configure--sharing-add">
                  <span className="create-cadence-configure--sharing-add-label">Add people</span>
                  <div className="create-cadence-configure--sharing-add-row">
                    <div className="create-cadence-configure--sharing-add-picker">
                      <UserSelector selected={pendingShareUsers} onChange={setPendingShareUsers} />
                    </div>
                    <select
                      className="create-cadence-configure--select create-cadence-configure--select-small"
                      value={pendingSharePermission}
                      onChange={(e) => setPendingSharePermission(e.target.value)}
                    >
                      <option value="view">Can view</option>
                      <option value="edit">Can edit</option>
                    </select>
                    <Button
                      type="secondary"
                      disabled={pendingShareUsers.length === 0}
                      onClick={() => {
                        const newUsers = pendingShareUsers
                          .map((u) => {
                            const userId = typeof u === 'object' ? u.value : u;
                            const label = typeof u === 'object' ? u.label : `User ${u}`;
                            return { userId, label, permission: pendingSharePermission };
                          })
                          .filter((u) => !sharedUsers.some((s) => String(s.userId) === String(u.userId)));
                        setSharedUsers([...sharedUsers, ...newUsers]);
                        setPendingShareUsers([]);
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* People with access */}
                {sharedUsers.length > 0 && (
                  <div className="create-cadence-configure--sharing-people">
                    <span className="create-cadence-configure--sharing-people-label">People with access</span>
                    {sharedUsers.map((share) => (
                      <div key={share.userId} className="create-cadence-configure--sharing-person-row">
                        <span className="create-cadence-configure--sharing-person-name">
                          {share.label || `User ${share.userId}`}
                        </span>
                        <select
                          className="create-cadence-configure--select create-cadence-configure--select-small"
                          value={share.permission}
                          onChange={(e) =>
                            setSharedUsers(
                              sharedUsers.map((s) =>
                                String(s.userId) === String(share.userId) ? { ...s, permission: e.target.value } : s
                              )
                            )
                          }
                        >
                          <option value="view">Can view</option>
                          <option value="edit">Can edit</option>
                        </select>
                        <button
                          type="button"
                          className="create-cadence-configure--sharing-remove-btn"
                          title="Remove access"
                          onClick={() =>
                            setSharedUsers(sharedUsers.filter((s) => String(s.userId) !== String(share.userId)))
                          }
                        >
                          <Icon icon="close" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="create-cadence-configure--divider" />

          {/* Linked Proposals — collapsible */}
          <div className="create-cadence-configure--subsection">
            <button
              type="button"
              className="create-cadence-configure--subsection-header"
              onClick={() => setIsProposalsOpen(!isProposalsOpen)}
            >
              <span className="create-cadence-configure--subsection-title">Linked Proposals</span>
              <Icon
                icon={isProposalsOpen ? 'chevron-up' : 'chevron-down'}
                className="create-cadence-configure--card-toggle-icon"
              />
            </button>
            {isProposalsOpen && (
              <div className="create-cadence-configure--subsection-body">
                <p className="create-cadence-configure--section-desc">
                  Connect this bulk message to proposals you&apos;re working to close
                </p>
                <ListboxMultiselect
                  size="medium"
                  emptyValue="Search and select proposals..."
                  label="Search and select proposals..."
                  options={proposals}
                  onChange={setLinkedProposals}
                  values={linkedProposals}
                  searchable={true}
                  onSearch={onProposalSearch}
                  showInfoBar={true}
                />
                {linkedProposals.length > 0 && (
                  <div className="create-cadence-configure--tags">
                    {linkedProposals.map((p) => (
                      <span key={p.value} className="create-cadence-configure--tag">
                        {p.label}
                        <button
                          type="button"
                          onClick={() => setLinkedProposals(linkedProposals.filter((lp) => lp.value !== p.value))}
                        >
                          <Icon icon="close" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="create-cadence-configure--divider" />

          {/* Cadence Blueprint — collapsible */}
          <div className="create-cadence-configure--subsection">
            <button
              type="button"
              className="create-cadence-configure--subsection-header"
              onClick={() => setIsBlueprintOpen(!isBlueprintOpen)}
            >
              <span className="create-cadence-configure--subsection-title">Cadence Blueprint</span>
              <Icon
                icon={isBlueprintOpen ? 'chevron-up' : 'chevron-down'}
                className="create-cadence-configure--card-toggle-icon"
              />
            </button>
            {isBlueprintOpen && (
              <div className="create-cadence-configure--subsection-body">
                <p className="create-cadence-configure--section-desc">
                  Link to a Cadence Blueprint for reporting and analytics
                </p>
                <div className="create-cadence-configure--template-search">
                  <div className="create-cadence-configure--template-input-row">
                    <input
                      type="text"
                      className="create-cadence-configure--input"
                      placeholder="Search cadence blueprints..."
                      value={templateSearch}
                      onChange={(e) => {
                        setTemplateSearch(e.target.value);
                        setTemplateDropdownOpen(true);
                      }}
                      onFocus={() => setTemplateDropdownOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setTemplateDropdownOpen(false);
                      }}
                    />
                    {templateDropdownOpen && (
                      <button
                        type="button"
                        className="create-cadence-configure--template-close-btn"
                        onClick={() => setTemplateDropdownOpen(false)}
                        aria-label="Close blueprint dropdown"
                      >
                        <Icon icon="close" />
                      </button>
                    )}
                  </div>
                  {templateDropdownOpen && (
                    <div className="create-cadence-configure--template-dropdown">
                      {cadenceShells
                        .filter((t) => t.name.toLowerCase().includes(templateSearch.toLowerCase()))
                        .map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className={`create-cadence-configure--template-dropdown-item ${cadenceTemplate === String(t.id) ? 'is-selected' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setCadenceTemplate(String(t.id));
                              setTemplateSearch(t.name);
                              setTemplateDropdownOpen(false);
                            }}
                          >
                            {t.name}
                          </button>
                        ))}
                      {cadenceShells.filter((t) => t.name.toLowerCase().includes(templateSearch.toLowerCase()))
                        .length === 0 && (
                        <div className="create-cadence-configure--template-dropdown-empty">
                          No cadence blueprints found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {cadenceTemplate && (
                  <button
                    type="button"
                    className="create-cadence-configure--unlink-btn"
                    onClick={() => {
                      setCadenceTemplate('');
                      setTemplateSearch('');
                    }}
                  >
                    Unlink from blueprint
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="create-cadence-configure--divider" />

          {/* Tags — collapsible */}
          <div className="create-cadence-configure--subsection">
            <button
              type="button"
              className="create-cadence-configure--subsection-header"
              onClick={() => setIsTagsOpen(!isTagsOpen)}
            >
              <span className="create-cadence-configure--subsection-title">Tags</span>
              <Icon
                icon={isTagsOpen ? 'chevron-up' : 'chevron-down'}
                className="create-cadence-configure--card-toggle-icon"
              />
            </button>
            {isTagsOpen && (
              <div className="create-cadence-configure--subsection-body">
                <p className="create-cadence-configure--section-desc">
                  Add tags to help organize and find your bulk messages
                </p>
                <div className="create-cadence-configure--add-row">
                  <div className="create-cadence-configure--tag-search">
                    <input
                      type="text"
                      className="create-cadence-configure--input"
                      placeholder="Add a tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                    />
                    {tagSuggestions.length > 0 && tagInput.length > 0 && (
                      <div className="create-cadence-configure--tag-dropdown">
                        {tagSuggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            className="create-cadence-configure--tag-dropdown-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelectTagSuggestion(suggestion);
                            }}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button type="action" onClick={handleAddTag} disabled={!tagInput.trim()}>
                    Add
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="create-cadence-configure--tags">
                    {tags.map((tag) => (
                      <span key={tag} className="create-cadence-configure--tag">
                        {tag}
                        <button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                          <Icon icon="close" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="create-cadence-configure--divider" />

          {/* Blackout Dates — collapsible */}
          <div className="create-cadence-configure--subsection">
            <button
              type="button"
              className="create-cadence-configure--subsection-header"
              onClick={() => setIsBlackoutOpen(!isBlackoutOpen)}
            >
              <span className="create-cadence-configure--subsection-title">Blackout Dates</span>
              <Icon
                icon={isBlackoutOpen ? 'chevron-up' : 'chevron-down'}
                className="create-cadence-configure--card-toggle-icon"
              />
            </button>
            {isBlackoutOpen && (
              <div className="create-cadence-configure--subsection-body">
                <p className="create-cadence-configure--section-desc">
                  If scheduled to do so, this bulk message will send on weekends, holidays, and/or during cadences and
                  task pauses. If you do not want this to be the case please check the option below.
                </p>
                <label className="bulk-message-audience-step--checkbox-label">
                  <input
                    type="checkbox"
                    checked={respectBlackoutDates}
                    onChange={(e) => setRespectBlackoutDates(e.target.checked)}
                  />
                  <span>Respect blackout dates and pauses</span>
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

BulkMessageAudienceStep.propTypes = {
  canDelete: PropTypes.bool,
  existingAudiences: PropTypes.array,
  audiencesLoaded: PropTypes.bool,
  onDisconnect: PropTypes.func.isRequired,
  onDisconnectAllManual: PropTypes.func.isRequired,
  contactLists: PropTypes.array.isRequired,
  savedSearches: PropTypes.array.isRequired,
  selectionMethod: PropTypes.object,
  setSelectionMethod: PropTypes.func.isRequired,
  selectedList: PropTypes.object,
  setSelectedList: PropTypes.func.isRequired,
  selectedSavedSearch: PropTypes.object,
  setSelectedSavedSearch: PropTypes.func.isRequired,
  selectedContacts: PropTypes.array.isRequired,
  setSelectedContacts: PropTypes.func.isRequired,
  successMetric: PropTypes.object,
  setSuccessMetric: PropTypes.func.isRequired,
  interactionTypes: PropTypes.array,
  portfolioTeams: PropTypes.object,
  isAdvancedOpen: PropTypes.bool.isRequired,
  setIsAdvancedOpen: PropTypes.func.isRequired,
  isSharingOpen: PropTypes.bool.isRequired,
  setIsSharingOpen: PropTypes.func.isRequired,
  pendingShareUsers: PropTypes.array.isRequired,
  setPendingShareUsers: PropTypes.func.isRequired,
  pendingSharePermission: PropTypes.string.isRequired,
  setPendingSharePermission: PropTypes.func.isRequired,
  sharedUsers: PropTypes.array.isRequired,
  setSharedUsers: PropTypes.func.isRequired,
  isProposalsOpen: PropTypes.bool.isRequired,
  setIsProposalsOpen: PropTypes.func.isRequired,
  proposals: PropTypes.array,
  linkedProposals: PropTypes.array.isRequired,
  setLinkedProposals: PropTypes.func.isRequired,
  onProposalSearch: PropTypes.func,
  isBlueprintOpen: PropTypes.bool.isRequired,
  setIsBlueprintOpen: PropTypes.func.isRequired,
  cadenceShells: PropTypes.array.isRequired,
  cadenceTemplate: PropTypes.string.isRequired,
  setCadenceTemplate: PropTypes.func.isRequired,
  templateSearch: PropTypes.string.isRequired,
  setTemplateSearch: PropTypes.func.isRequired,
  templateDropdownOpen: PropTypes.bool.isRequired,
  setTemplateDropdownOpen: PropTypes.func.isRequired,
  isTagsOpen: PropTypes.bool.isRequired,
  setIsTagsOpen: PropTypes.func.isRequired,
  tagInput: PropTypes.string.isRequired,
  setTagInput: PropTypes.func.isRequired,
  tagSuggestions: PropTypes.array.isRequired,
  handleAddTag: PropTypes.func.isRequired,
  handleSelectTagSuggestion: PropTypes.func.isRequired,
  tags: PropTypes.array.isRequired,
  setTags: PropTypes.func.isRequired,
  isBlackoutOpen: PropTypes.bool.isRequired,
  setIsBlackoutOpen: PropTypes.func.isRequired,
  respectBlackoutDates: PropTypes.bool.isRequired,
  setRespectBlackoutDates: PropTypes.func.isRequired,
};

export default BulkMessageAudienceStep;
